import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { type BrandDraft, BrandExtractCard } from "./BrandExtractCard";
import { BrandForm } from "./BrandForm";

/** A Build with AI draft opened in the create form ("Open in form" on its card). */
export interface BrandFormState {
  draft: BrandDraft;
  brandStyle?: string;
  /** The chat the draft came from; the created brand is linked to it. */
  sourceSessionUuid?: string;
}

/**
 * /build/brands/new — the create page, with an inline "extract from a
 * guidelines doc" card that pre-fills the identity fields; the extracted
 * palette/fonts/tone are carried into the new brand on create.
 */
export function NewBrandPage() {
  const { t } = useTranslation("brands");
  const areas = useAreaCrumbs();
  const fromChat = (useLocation().state as BrandFormState | null) ?? undefined;
  const [draft, setDraft] = useState<BrandDraft | null>(fromChat?.draft ?? null);

  return (
    <div className="flex flex-col">
      <PageBreadcrumbs trail={[areas.build, { label: t("title"), to: "/build/brands" }]} current={t("form.create")} />
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6 md:px-8">
        <BrandExtractCard onDraft={setDraft} />
        {/* key: remount the form with the extracted identity as defaults. */}
        <BrandForm
          key={draft ? JSON.stringify([draft.name, draft.description, draft.audience]) : "blank"}
          draft={draft ?? undefined}
          brandStyle={fromChat?.brandStyle}
          sourceSessionUuid={fromChat?.sourceSessionUuid}
        />
      </div>
    </div>
  );
}
